import React, { FC, useEffect, useState } from 'react';

import {
  NativeResources,
  PackageCategory,
  VulnerabilitySeverity,
} from '@ariksa/inventory-core';
import { Box, HStack } from '@chakra-ui/react';
import { map } from 'lodash';

import { renderSeverityBar } from 'components/DataDisplay';
import { Select } from 'components/DataEntry';
import {
  applicationOptions,
  exploitableOptions,
  internetExposureOptions,
  libraryOptions,
  osOptions,
  packageTypeOptions,
  patchAvailableOptions,
  vulnerabilitySeverityOptions,
} from 'containers/Visibility/Vulnerabilities/Components/utils';
import { selectVulnerabilities } from 'containers/Visibility/Vulnerabilities/selectors';
import { VulnerabilityViewByTabs } from 'containers/Visibility/Vulnerabilities/types';

interface Props {
  isLoading: boolean;
  isPatchable: Record<string, any>;

  updateValue(field: string, value: any);

  isExploitable?: Record<string, any>;

  isInternetExposed?: Record<string, any>;

  packageType?: Record<string, any>;

  packageName?: Record<string, any>;

  severity: Record<string, any>;
  severities?: Record<string, any>[];

  showPackageType?: boolean;
  // Hide "Has Public Exploit" where the caller can't apply it.
  showExploitable?: boolean;
  // ScanVault shows internet exposure only as risk context.
  showInternetExposure?: boolean;
  selectedTab?: string;
  resourceType?: string;
}

export const VulnerabilityFiltersMenu: FC<Props> = props => {
  const {
    isPatchable,
    packageType,
    packageName,
    severity = '',
    severities = [],
    isLoading = false,
    isExploitable,
    isInternetExposed,
    updateValue,
    showPackageType = true,
    // Default to shown (opt-out like showPackageType); without a default the
    // exploit filter vanished from callers that don't pass it.
    showExploitable = true,
    showInternetExposure = true,
    selectedTab,
    resourceType,
  } = props;

  const [showPackageName, setShowPackageName] = useState(!!packageType?.value);
  const [packageNameOptions, setPackageNameOptions] = useState<
    Record<string, any>[]
  >([]);

  useEffect(() => {
    const options =
      packageType?.value === PackageCategory.OsPackage
        ? osOptions
        : packageType?.value === PackageCategory.ApplicationPackage
        ? applicationOptions
        : packageType?.value === PackageCategory.LibraryPackage
        ? libraryOptions
        : [];
    setPackageNameOptions(options);
    updateValue?.('packageName', options[0]);
  }, [packageType, updateValue]);

  const severityOptions = map(VulnerabilitySeverity, o => ({
    label: renderSeverityBar({ value: o }),
    value: o,
  }));

  return (
    <HStack>
      {selectedTab === VulnerabilityViewByTabs.Package ? (
        <Box maxW={64}>
          <Select
            isAFilter
            options={severityOptions}
            value={severities}
            onChange={s => updateValue('severities', s)}
            isMulti
            isDisabled={isLoading}
            menuPortalTarget={document.body}
            leftMessage="Severity"
            showUndefinedValueLabel={false}
            showTotalSelected
          />
        </Box>
      ) : (
        <Box>
          <Select
            isAFilter
            options={vulnerabilitySeverityOptions}
            value={severity}
            onChange={s => updateValue('severity', s)}
            isDisabled={isLoading}
            menuPortalTarget={document.body}
            placeholder="Severity"
            leftMessage="Severity"
            showUndefinedValueLabel={false}
            optionIconWidth={7}
          />
        </Box>
      )}
      {showExploitable && (
        <Box zIndex={900}>
          <Select
            isAFilter
            options={exploitableOptions}
            value={isExploitable}
            onChange={s => updateValue('isExploitable', s)}
            showIconInValueContainer
            placeholder="Has Public Exploit"
            leftMessage="Has Public Exploit"
            isDisabled={isLoading}
            menuPortalTarget={document.body}
            showUndefinedValueLabel={false}
          />
        </Box>
      )}

      {selectedTab !== 'cve' && (
        <>
          <Box>
            <Select
              isAFilter
              options={patchAvailableOptions}
              value={isPatchable}
              onChange={s => updateValue('isPatchable', s)}
              placeholder="Has Patch"
              leftMessage="Has Patch"
              isDisabled={isLoading}
              menuPortalTarget={document.body}
              showUndefinedValueLabel={false}
            />
          </Box>
        </>
      )}
      {showInternetExposure &&
        (selectedTab === VulnerabilityViewByTabs.Package ||
          (selectedTab === VulnerabilityViewByTabs.Resource &&
            resourceType !== NativeResources.ElasticContainerRegistry)) && (
          <Box>
            <Select
              isAFilter
              options={internetExposureOptions}
              value={isInternetExposed}
              onChange={s => updateValue('isInternetExposed', s)}
              showIconInValueContainer
              leftMessage="Internet Exposure"
              isDisabled={isLoading}
              menuPortalTarget={document.body}
              showUndefinedValueLabel={false}
            />
          </Box>
        )}
      {/*{showPackageType && selectedTab === VulnerabilityViewByTabs.Package && (
        <>
          <Box>
            <Select
              isAFilter
              options={packageTypeOptions(selectedTab)}
              value={packageType}
              onChange={s => {
                updateValue?.('packageType', s);
                setShowPackageName(!!s.value);
              }}
              leftMessage="Package Type"
              showUndefinedValueLabel={false}
              isDisabled={isLoading}
              menuPortalTarget={document.body}
            />
          </Box>
          {showPackageName &&
            selectedTab !== NativeResources.ElasticContainerRegistry && (
              <Box zIndex={900}>
                <Select
                  options={packageNameOptions}
                  value={packageName}
                  onChange={s => updateValue('packageName', s)}
                  showIconInValueContainer
                  showUndefinedValueLabel={false}
                  isAFilter
                  leftMessage="Package Name"
                  isDisabled={isLoading}
                  menuPortalTarget={document.body}
                />
              </Box>
            )}
        </>
      )}*/}
    </HStack>
  );
};
